import React, { useEffect, useState } from "react";
import { Gift, Copy, Check, Users, Sparkles } from "lucide-react";
import { Page, Card, StatCard, PrimaryButton } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";

export default function ReferralRewards() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referral-rewards/my-link").then(({ data }) => setData(data));
  }, []);

  const fullLink = data ? `${window.location.origin}${data.link}` : "";

  const copy = () => {
    navigator.clipboard.writeText(fullLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard");
  };

  if (!data) return <Page title="Referral Rewards"><div className="mf-skeleton h-48"></div></Page>;

  return (
    <Page title="Referral Rewards" subtitle="Refer a colleague — you both get a free month.">
      <Card delay={0.05} className="mb-5" testid="reward-banner">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "var(--mf-accent)" }}>
            <Gift className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-serif text-2xl mb-1">Earn free months</h2>
            <p className="text-sm" style={{ color: "var(--mf-text-muted)" }}>When a colleague signs up using your link, you both get a free month of Clinician Well. No cap. No expiry.</p>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-4 mb-5">
        <StatCard label="Free Months Earned" value={data.free_months_earned} icon={Sparkles} accent="var(--mf-accent)" delay={0.1} testid="ref-earned" />
        <StatCard label="Total Referrals" value={data.total_referrals} icon={Users} accent="var(--mf-primary)" delay={0.15} testid="ref-total" />
        <StatCard label="Pending" value={data.pending} icon={Users} accent="var(--mf-secondary)" delay={0.2} testid="ref-pending" />
      </div>

      <Card delay={0.25} testid="reward-link">
        <h3 className="font-serif text-xl mb-3">Your unique link</h3>
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <input readOnly value={fullLink} className="flex-1 px-4 py-3 rounded-xl outline-none text-sm font-mono" style={{ background: "var(--mf-muted)" }} data-testid="ref-link-input" />
          <button onClick={copy} className="mf-btn-primary flex items-center gap-2" data-testid="ref-copy-btn">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Share via email, Slack, or post on professional networks. Your code: <code className="font-mono">{data.code}</code></div>
      </Card>

      <Card delay={0.3} className="mt-5" testid="reward-tips">
        <h3 className="font-serif text-xl mb-3">Where to share</h3>
        <ul className="space-y-2 text-sm" style={{ color: "var(--mf-text-muted)" }}>
          <li>• Email signature — append to every outgoing message</li>
          <li>• Therapist Facebook groups & subreddits</li>
          <li>• Post on LinkedIn with a brief story of how Clinician Well saved you 10 hours/week</li>
          <li>• Recommend at supervision groups, CE workshops, peer consultations</li>
        </ul>
      </Card>
    </Page>
  );
}
